/**
 * Operator: fleet view (plan §18.2, §26).
 *
 * Deployments by state, with the resource identity an operator needs to find a tenant in the
 * provider consoles. This is the ONE place infrastructure detail is legitimately visible
 * (API-CONTRACT §8) — it is behind the operator gate and never rendered for a customer.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/client.js";
import { Empty, Loading, formatDate } from "../../components/ui.js";
import { AdminError, AdminNav } from "./shared.js";
import type { AdminDeployment, DeploymentStatus } from "../../api/types.js";

const STATUSES: (DeploymentStatus | "")[] = [
  "",
  "QUEUED",
  "PROVISIONING",
  "PROVISIONING_FAILED",
  "VERIFYING",
  "VERIFYING_FAILED",
  "READY",
  "SUSPENDED",
  "CANCELLATION_PENDING",
  "RETAINED",
  "DELETION_PENDING",
];

export default function AdminDeployments() {
  const [rows, setRows] = useState<AdminDeployment[] | null>(null);
  const [status, setStatus] = useState<DeploymentStatus | "">("");
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminDeployments(status ? { status } : {});
      setRows(res.deployments as AdminDeployment[]);
      setError(null);
    } catch (e) {
      setError(e);
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Operator</p>
        <h1>Fleet</h1>
      </div>
      <AdminNav />

      <div className="row" style={{ marginBottom: 20 }}>
        <label className="field" style={{ marginTop: 0, minWidth: 240 }}>
          <span className="field__label">Filter by state</span>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as DeploymentStatus | "")}>
            {STATUSES.map((s) => (
              <option key={s || "all"} value={s}>
                {s || "All states"}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <AdminError error={error} onRetry={() => void load()} /> : null}
      {loading ? <Loading label="Loading the fleet" /> : null}

      {!loading && rows && rows.length === 0 ? (
        <Empty title="No deployments match">
          <p>Nothing in this state right now.</p>
        </Empty>
      ) : null}

      {!loading && rows && rows.length > 0 ? (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Owner UID</th>
                <th scope="col">State</th>
                <th scope="col">Plan</th>
                <th scope="col">Region</th>
                <th scope="col">Release</th>
                <th scope="col">Last verified</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id}>
                  <td className="mono small">{d.ownerUid}</td>
                  <td className="mono small">{d.status}</td>
                  <td className="muted small">{d.planCode}</td>
                  <td className="muted small">{d.regionCode}</td>
                  <td className="muted mono small">{d.platformRelease ?? "—"}</td>
                  <td className="muted mono small">{formatDate(d.lastVerifiedAt)}</td>
                  <td>
                    <Link className="btn btn--ghost btn--sm" to={`/admin/deployments/${d.id}`}>
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
